import "./background.component.css"
import { ReactNode } from "react";
import GreenBlub from "../assets/green_blub_1.png";
import GreenBlub2 from "../assets/green_blub_2.png";
import Lines from "../assets/lines_3.svg";
import Lines2 from "../assets/lines_4.svg";

//Settings
const blobConcentration = 900; //Every x pixel blobs will spawn

const dotsConcentration = 450; //Every x pixel dots will spawn
const dotsSize = { min: 60, max: 150 }; //From x to y
const dotsOpacity = 0.04; //0 to 1 (can be a double number)

const linesConcentration = 1700; //Every x pixel lines will spawn
const linesRotation = 20; //Max positive & negative rotation (e. g. -20 to 20)
const linesOpacity = 0.04; //0 to 1 (can be a double number)

//Do not edit
let finalSeed: number; //Automatic seed if no seed is set

/**
 * Background component
 * @param seed Randomizer seed
 * @param className Further class names
 * @param children Main site content
 * @param key Further keys
 * @return Background component
 */
function Background(
    props: Readonly<{
        seed?: number,

        className?: string,
        children?: ReactNode,
        [key: string]: any
    }>
) {
    const { seed, className, children, ...overflowProps } = props;

    if (!seed) {
        finalSeed = randomNumber(10000, 99999);
        console.log(`Background seed: ${finalSeed}`)
    } else {
        finalSeed = seed;
    }

    const randomBinary = randomNumber(0, 1, finalSeed);

    return (
        <div className={`background ${className ?? ""}`} {...overflowProps}>
            {children} {/* Main site content */}
            
            <div className="elements">
                {/* Blubs */}
                {[...Array(Math.ceil(document.documentElement.scrollHeight / blobConcentration))].map((v, i) => {
                    const randomRotation = randomNumber(0, 360, finalSeed * (i + 2)); //All numbers but 0 and 1
                    const randomBlub = randomNumber(0, 1, finalSeed * (i + 2)) ? GreenBlub : GreenBlub2;
                    
                    return <img
                        key={i}
                        alt="blub"
                        src={randomBlub}
                        style={{
                            top: i * blobConcentration,

                            ...((randomBinary ? !(i % 2) : i % 2) ? {
                                right: 0,
                                transform: `translateX(200px) rotate(${randomRotation}deg)`
                            } : {
                                left: 0,
                                transform: `translateX(-200px) rotate(${randomRotation}deg)`
                            })
                        }}
                    />
                })}

                {/* Dots */}
                {[...Array(Math.ceil(document.documentElement.scrollHeight / dotsConcentration) - 1)].map((v, i) => {
                    const fullSize = randomNumber(dotsSize.min, dotsSize.max, finalSeed * (i + 2));
                    const dotSize = fullSize / 7;
                    const yPos = randomNumber(0, 100, (finalSeed * (i + 2)));
                    
                    return <div
                        className="dots"
                        key={i}
                        style={{
                            top: i * dotsConcentration + dotsConcentration,
                            width: fullSize,
                            height: fullSize,
                            left: `calc(${yPos}% - ${fullSize}px / 2)`
                        }}
                    >
                        <div>
                            {[...Array(3)].map((v2, i2) =>
                                <div key={i2}>
                                    {[...Array(3)].map((v3, i3) =>
                                        <div key={i3} style={{ width: dotSize, height: dotSize, opacity: dotsOpacity }} />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>;
                })}

                {/* Lines */}
                {[...Array(Math.ceil(document.documentElement.scrollHeight / linesConcentration))].map((v, i) => {
                    const randomOrientation = randomNumber(linesRotation, linesRotation * -1, finalSeed * (i + 2));
                    const randomLines = randomNumber(0, 1, finalSeed * (i + 2)) ? Lines : Lines2;
                    const randomFlip = randomNumber(0, 1, finalSeed * (i + 2)) ? !(i % 2) : i % 2;
                    
                    return <img
                        key={i}
                        alt="lines"
                        src={randomLines}
                        style={{
                            top: i * linesConcentration,
                            width: `${100 + Math.abs(randomOrientation)}vw`, //"Math.abs" makes negative numbers positive
                            opacity: linesOpacity,

                            ...(randomFlip ? {
                                transform: `rotate(${randomOrientation}deg)`
                            } : {
                                transform: `rotate(${randomOrientation}deg) scale(-1)`
                            })
                        }}
                    />
                })}
            </div>
        </div>
    )
}

export default Background;

/**
 * Random number generator
 * @param min From
 * @param max To
 * @param seed
 * @returns Random number
 */
function randomNumber(min: number, max: number, seed?: number) {
    if (seed || finalSeed) {
        let tempSeed = seed ?? finalSeed ?? 0; //0 Shuld never happen
        const x = Math.sin(tempSeed++) * 10000;
        const normalized = x - Math.floor(x);
        return min + Math.floor(normalized * (max - min + 1));
    }
    
    return Math.floor(Math.random() * (max - min + 1)) + min;
}